// src/pages/AdminChartDashboard.js
// Admin-only chart staging dashboard
// Shows all charts grouped by role level (Member → Leader → Consultant)
// Use this to decide which charts belong in each role's view before rolling out.

import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, RadarChart, Radar, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Legend, PieChart, Pie, Cell, LineChart, Line,
} from 'recharts';
import {
  collection, query, where, getDocs, doc, getDoc,
  orderBy, limit,
} from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_CATEGORIES, withCurrentLabels } from '../utils/surveyQuestions';
import { BACKTICK_CODES } from '../utils/shortcodes';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normalizeScore(score) {
  return Math.round(((score - 1) / 4) * 100);
}

function scoreColor(score) {
  if (score >= 4) return '#2d6a4f';
  if (score >= 3) return '#f4a261';
  return '#e63946';
}

function SectionHeader({ children }) {
  return (
    <div style={{
      fontSize: '0.68rem',
      fontWeight: 800,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      color: 'var(--ink-muted)',
      borderBottom: '1px solid var(--border)',
      paddingBottom: 8,
      marginBottom: 20,
      marginTop: 36,
    }}>
      {children}
    </div>
  );
}

function RolePill({ label, active, onClick, color }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '8px 20px',
        borderRadius: 100,
        border: `2px solid ${active ? color : 'var(--border)'}`,
        background: active ? color : 'transparent',
        color: active ? 'white' : 'var(--ink-muted)',
        fontWeight: 700,
        fontSize: '0.85rem',
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
    >
      {label}
    </button>
  );
}

function EmptyState({ message }) {
  return (
    <div style={{
      padding: '24px',
      background: 'var(--bg-soft)',
      borderRadius: 8,
      textAlign: 'center',
      color: 'var(--ink-muted)',
      fontSize: '0.85rem',
      marginBottom: 24,
    }}>
      {message}
    </div>
  );
}

// ─── Chart Components ─────────────────────────────────────────────────────────

function MyScoreVsTeamAverage({ responses, categories, userId }) {
  const myResponse = responses.find(r => r.userId === userId);
  if (!myResponse) return <EmptyState message="No personal assessment found for this user in this team." />;

  const data = categories.map(cat => {
    const myScores = cat.questions.map(q => myResponse.answers?.[q.id]).filter(Boolean);
    const myAvg = myScores.length ? myScores.reduce((a, b) => a + b, 0) / myScores.length : 0;

    const teamScores = [];
    responses.forEach(r => {
      cat.questions.forEach(q => {
        if (r.answers?.[q.id] != null) teamScores.push(r.answers[q.id]);
      });
    });
    const teamAvg = teamScores.length ? teamScores.reduce((a, b) => a + b, 0) / teamScores.length : 0;

    return {
      name: cat.label.split(' ').slice(0, 2).join(' '),
      'My Score': +myAvg.toFixed(2),
      'Team Average': +teamAvg.toFixed(2),
    };
  });

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
        <Bar dataKey="My Score" fill="#2a3d5b" radius={[4, 4, 0, 0]} />
        <Bar dataKey="Team Average" fill="#cba43a" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function TeamRadarOverview({ responses, categories }) {
  const data = categories.map(cat => {
    const scores = [];
    responses.forEach(r => {
      cat.questions.forEach(q => {
        if (r.answers?.[q.id] != null) scores.push(r.answers[q.id]);
      });
    });
    const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    return { category: cat.label.split(' ').slice(0, 2).join(' '), score: +avg.toFixed(2), fullMark: 5 };
  });

  return (
    <ResponsiveContainer width="100%" height={280}>
      <RadarChart data={data}>
        <PolarGrid stroke="var(--border)" />
        <PolarAngleAxis dataKey="category" tick={{ fontSize: 11 }} />
        <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 9 }} />
        <Radar name="Team Score" dataKey="score" stroke="#2a3d5b" fill="#2a3d5b" fillOpacity={0.25} strokeWidth={2} />
        <Tooltip formatter={v => v.toFixed(2)} />
      </RadarChart>
    </ResponsiveContainer>
  );
}

function CategoryBreakdownBars({ responses, categories }) {
  const data = categories.map(cat => {
    const scores = [];
    responses.forEach(r => {
      cat.questions.forEach(q => {
        if (r.answers?.[q.id] != null) scores.push(r.answers[q.id]);
      });
    });
    const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    return { name: cat.label, score: +avg.toFixed(2), color: cat.color };
  });

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-15} textAnchor="end" height={50} />
        <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
        <Tooltip formatter={v => v.toFixed(2)} />
        <Bar dataKey="score" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => <Cell key={i} fill={d.color} />)}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

function MemberCompletionScores({ responses, members, categories }) {
  const memberData = members.map(m => {
    const resp = responses.find(r => r.userId === m.id);
    if (!resp) return { name: m.displayName || m.email, completed: false, avg: null };
    const allScores = [];
    categories.forEach(cat => {
      cat.questions.forEach(q => {
        if (resp.answers?.[q.id] != null) allScores.push(resp.answers[q.id]);
      });
    });
    const avg = allScores.length ? allScores.reduce((a, b) => a + b, 0) / allScores.length : null;
    return { name: (m.displayName || m.email).split('@')[0], completed: true, avg: +avg.toFixed(2) };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {memberData.map((m, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '10px 14px', background: 'var(--bg-soft)', borderRadius: 8,
        }}>
          <span style={{
            fontSize: '0.72rem', fontWeight: 700,
            color: m.completed ? '#2d6a4f' : 'var(--ink-muted)',
          }}>
            {m.completed ? '✓' : '○'}
          </span>
          <span style={{ flex: 1, fontSize: '0.88rem', fontWeight: 500 }}>{m.name}</span>
          {m.avg != null && (
            <span style={{
              fontFamily: 'var(--font-display)',
              fontSize: '1.1rem',
              fontWeight: 700,
              color: scoreColor(m.avg),
            }}>
              {m.avg}
              <span style={{ fontSize: '0.65rem', color: 'var(--ink-muted)', fontFamily: 'var(--font-body)' }}>/5</span>
            </span>
          )}
          {!m.completed && (
            <span style={{ fontSize: '0.72rem', color: 'var(--ink-muted)' }}>Not submitted</span>
          )}
        </div>
      ))}
    </div>
  );
}

function PerceivedVsObservedChart({ teamScores, obsRates, categories, meetingCount }) {
  const data = categories.map(cat => ({
    name: cat.label.split(' ').slice(0, 2).join(' '),
    'Team Perspective': teamScores[cat.id] != null ? normalizeScore(teamScores[cat.id]) : null,
    'Consultant Observed': obsRates[cat.id] != null ? Math.round(obsRates[cat.id] * 100) : null,
  }));

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
        <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 10 }} />
        <Tooltip formatter={v => `${v}%`} />
        <Legend wrapperStyle={{ fontSize: '0.78rem' }} />
        <Bar dataKey="Team Perspective" fill="#2a3d5b" radius={[4, 4, 0, 0]} />
        <Bar dataKey="Consultant Observed" fill="#cba43a" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function WorkingGeniusByMeeting({ meetings }) {
  const geniusCodes = Object.keys(BACKTICK_CODES);
  const data = meetings.map(m => {
    const row = { name: (m.name || 'Meeting').substring(0, 14) };
    geniusCodes.forEach(code => { row[code] = 0; });
    (m.entries || []).forEach(entry => {
      const text = entry.text || '';
      geniusCodes.forEach(code => {
        const pattern = new RegExp('`' + code.replace('`', ''), 'g');
        const matches = text.match(pattern);
        if (matches) row[code] += matches.length;
      });
    });
    return row;
  });

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -10, bottom: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
        <YAxis tick={{ fontSize: 10 }} />
        <Tooltip />
        <Legend wrapperStyle={{ fontSize: '0.75rem' }} />
        {geniusCodes.map(code => (
          <Bar key={code} dataKey={code} stackId="a"
            fill={BACKTICK_CODES[code]?.color || '#999'}
            name={BACKTICK_CODES[code]?.label || code}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

function ObservationRatesByCategory({ obsRates, categories, meetingCount }) {
  const data = categories.map(cat => ({
    name: cat.label,
    rate: obsRates[cat.id] != null ? Math.round(obsRates[cat.id] * 100) : 0,
    color: cat.color,
  }));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {data.map((d, i) => (
        <div key={i}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>{d.name}</span>
            <span style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}>{d.rate}%</span>
          </div>
          <div style={{ height: 8, background: 'var(--border)', borderRadius: 4 }}>
            <div style={{
              height: '100%', width: `${d.rate}%`,
              background: d.color, borderRadius: 4,
              transition: 'width 0.5s ease',
            }} />
          </div>
        </div>
      ))}
      {meetingCount > 0 && (
        <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 4 }}>
          Based on {meetingCount} meeting{meetingCount !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function AdminChartDashboard() {
  const { currentUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [roleView, setRoleView] = useState('member'); // 'member' | 'leader' | 'consultant'
  const [loading, setLoading] = useState(false);

  // Data
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [responses, setResponses] = useState([]);
  const [members, setMembers] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [observations, setObservations] = useState([]);
  const [teamScores, setTeamScores] = useState({});
  const [obsRates, setObsRates] = useState({});

  // Load team list on mount
  useEffect(() => {
    async function loadTeams() {
      const snap = await getDocs(collection(db, 'teams'));
      const teamList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTeams(teamList);
      if (teamList.length > 0) setSelectedTeamId(teamList[0].id);
    }
    loadTeams();
  }, []);

  // Load team data when selection changes
  useEffect(() => {
    if (selectedTeamId) loadTeamData(selectedTeamId);
  }, [selectedTeamId]);

  async function loadTeamData(teamId) {
    setLoading(true);
    try {
      // Team + custom questions
      const teamSnap = await getDoc(doc(db, 'teams', teamId));
      let cats = DEFAULT_CATEGORIES;
      if (teamSnap.exists() && teamSnap.data().customQuestions) {
        cats = withCurrentLabels(teamSnap.data().customQuestions);
      }
      setCategories(cats);

      // Members
      const usersSnap = await getDocs(
        query(collection(db, 'users'), where('teamId', '==', teamId))
      );
      const memberList = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMembers(memberList);

      // Assessment responses
      const responsesSnap = await getDocs(
        query(collection(db, 'responses'), where('teamId', '==', teamId))
      );
      const responseList = responsesSnap.docs.map(d => d.data());
      setResponses(responseList);

      // Compute team scores
      const tScores = {};
      cats.forEach(cat => {
        const scores = [];
        responseList.forEach(r => {
          cat.questions.forEach(q => {
            if (r.answers?.[q.id] != null) scores.push(r.answers[q.id]);
          });
        });
        tScores[cat.id] = scores.length
          ? scores.reduce((a, b) => a + b, 0) / scores.length
          : null;
      });
      setTeamScores(tScores);

      // Last 5 completed meetings
      const meetingsSnap = await getDocs(
        query(
          collection(db, 'meetings'),
          where('teamId', '==', teamId),
          where('status', 'in', ['completed', 'reviewed']),
          orderBy('meetingDate', 'desc'),
          limit(5)
        )
      );
      const meetingList = meetingsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setMeetings(meetingList);

      // Consultant observations
      let obsList = [];
      if (meetingList.length > 0) {
        const meetingIds = meetingList.map(m => m.id);
        const obsSnap = await getDocs(
          query(
            collection(db, 'consultantObservations'),
            where('meetingId', 'in', meetingIds)
          )
        );
        obsList = obsSnap.docs.map(d => d.data());
      }
      setObservations(obsList);

      // Compute observation rates
      const oRates = {};
      cats.forEach(cat => {
        const rates = [];
        cat.questions.forEach(q => {
          let observedCount = 0;
          let total = 0;
          obsList.forEach(obs => {
            if (obs.observed && obs.observed[q.id] !== undefined) {
              total++;
              if (obs.observed[q.id]) observedCount++;
            }
          });
          if (total > 0) rates.push(observedCount / total);
        });
        oRates[cat.id] = rates.length
          ? rates.reduce((a, b) => a + b, 0) / rates.length
          : null;
      });
      setObsRates(oRates);

    } catch (err) {
      console.error('Error loading team data:', err);
    } finally {
      setLoading(false);
    }
  }

  const selectedTeam = teams.find(t => t.id === selectedTeamId);
  const completedCount = responses.length;
  const memberCount = members.length;

  const roleColors = {
    member: '#2a3d5b',
    leader: '#5f7942',
    consultant: '#cba43a',
  };

  return (
    <div className="fade-up">
      {/* Header */}
      <div className="page-header">
        <h2>Admin Chart Dashboard</h2>
        <p>Preview all charts by role level. Use this to decide what each role should see.</p>
      </div>

      {/* Team selector */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ fontSize: '0.78rem', color: 'var(--ink-muted)', fontWeight: 600, display: 'block', marginBottom: 6 }}>
              SELECT TEAM
            </label>
            <select
              value={selectedTeamId}
              onChange={e => setSelectedTeamId(e.target.value)}
              style={{
                width: '100%', padding: '10px 14px',
                border: '1.5px solid var(--border)',
                borderRadius: 8, fontSize: '0.95rem',
                background: 'var(--bg-card)', color: 'var(--ink)',
                cursor: 'pointer',
              }}
            >
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          {selectedTeam && (
            <div style={{ display: 'flex', gap: 20, fontSize: '0.82rem', color: 'var(--ink-muted)' }}>
              <span>👥 {memberCount} members</span>
              <span>✓ {completedCount} assessments</span>
              <span>📋 {meetings.length} meetings</span>
              <span>🔍 {observations.length} observations</span>
            </div>
          )}
        </div>
      </div>

      {/* Role toggle */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 28, flexWrap: 'wrap' }}>
        {[
          { id: 'member', label: '👤 Member View' },
          { id: 'leader', label: '⭐ Leader View' },
          { id: 'consultant', label: '🔍 Consultant View' },
        ].map(r => (
          <RolePill
            key={r.id}
            label={r.label}
            active={roleView === r.id}
            onClick={() => setRoleView(r.id)}
            color={roleColors[r.id]}
          />
        ))}
      </div>

      {loading && (
        <p style={{ color: 'var(--ink-muted)', padding: '24px 0' }}>Loading chart data...</p>
      )}

      {!loading && selectedTeamId && (
        <>

          {/* ── MEMBER CHARTS ── */}
          <SectionHeader>
            {roleView === 'member' ? '👤 Member Charts' : roleView === 'leader' ? '👤 Member Charts (also shown to Leader)' : '👤 Member Charts (also shown to Leader + Consultant)'}
          </SectionHeader>

          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-title">My Score vs Team Average</div>
            <div className="card-subtitle">Personal assessment score compared to the team's average across all categories</div>
            <MyScoreVsTeamAverage
              responses={responses}
              categories={categories}
              userId={currentUser?.uid}
            />
          </div>

          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-title">Assessment Completion</div>
            <div className="card-subtitle">Which team members have submitted their assessment</div>
            {members.length > 0
              ? <MemberCompletionScores responses={responses} members={members} categories={categories} />
              : <EmptyState message="No members found for this team." />
            }
          </div>

          {/* ── LEADER CHARTS ── */}
          {(roleView === 'leader' || roleView === 'consultant') && (
            <>
              <SectionHeader>
                {roleView === 'leader' ? '⭐ Leader Charts' : '⭐ Leader Charts (also shown to Consultant)'}
              </SectionHeader>

              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-title">Team Radar Overview</div>
                <div className="card-subtitle">Aggregate team score across all assessment categories</div>
                {responses.length > 0
                  ? <TeamRadarOverview responses={responses} categories={categories} />
                  : <EmptyState message="No assessment responses yet." />
                }
              </div>

              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-title">Category Breakdown</div>
                <div className="card-subtitle">Average team score per category</div>
                {responses.length > 0
                  ? <CategoryBreakdownBars responses={responses} categories={categories} />
                  : <EmptyState message="No assessment responses yet." />
                }
              </div>

              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-title">Team Perspective vs Consultant Observed</div>
                <div className="card-subtitle">Assessment scores normalized to 0–100% vs consultant observation rates across last 5 meetings</div>
                {responses.length > 0 && observations.length > 0
                  ? <PerceivedVsObservedChart
                      teamScores={teamScores}
                      obsRates={obsRates}
                      categories={categories}
                      meetingCount={meetings.length}
                    />
                  : <EmptyState message="Needs both assessment responses and consultant observations." />
                }
              </div>
            </>
          )}

          {/* ── CONSULTANT CHARTS ── */}
          {roleView === 'consultant' && (
            <>
              <SectionHeader>🔍 Consultant Charts</SectionHeader>

              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-title">Observation Rates by Category</div>
                <div className="card-subtitle">How often each category's behaviors were observed across the last 5 meetings</div>
                {observations.length > 0
                  ? <ObservationRatesByCategory
                      obsRates={obsRates}
                      categories={categories}
                      meetingCount={meetings.length}
                    />
                  : <EmptyState message="No consultant observations recorded yet." />
                }
              </div>

              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-title">Working Genius by Meeting</div>
                <div className="card-subtitle">Stacked distribution of Working Genius codes recorded across the last 5 meetings</div>
                {meetings.length > 0
                  ? <WorkingGeniusByMeeting meetings={meetings} />
                  : <EmptyState message="No completed meetings recorded yet." />
                }
              </div>

              <div className="card" style={{ marginBottom: 20 }}>
                <div className="card-title">Meeting Activity Summary</div>
                <div className="card-subtitle">Entries, duration, and participants across recent meetings</div>
                {meetings.length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid var(--border)' }}>
                          {['Meeting', 'Date', 'Attendees', 'Entries', 'Has Observation'].map(col => (
                            <th key={col} style={{
                              padding: '8px 12px', textAlign: 'left',
                              fontSize: '0.72rem', fontWeight: 700,
                              textTransform: 'uppercase', letterSpacing: '0.07em',
                              color: 'var(--ink-muted)',
                            }}>
                              {col}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {meetings.map((m, i) => {
                          const hasObs = observations.some(o => o.meetingId === m.id);
                          const date = m.meetingDate?.toDate
                            ? m.meetingDate.toDate().toLocaleDateString()
                            : m.meetingDate
                            ? new Date(m.meetingDate).toLocaleDateString()
                            : '—';
                          return (
                            <tr key={m.id} style={{
                              borderBottom: '1px solid var(--border)',
                              background: i % 2 === 0 ? 'transparent' : 'var(--bg-soft)',
                            }}>
                              <td style={{ padding: '10px 12px', fontWeight: 600 }}>{m.name}</td>
                              <td style={{ padding: '10px 12px', color: 'var(--ink-muted)' }}>{date}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>{m.attendees?.length || 0}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>{m.entries?.length || 0}</td>
                              <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                                <span style={{ color: hasObs ? '#2d6a4f' : 'var(--ink-muted)', fontWeight: 700 }}>
                                  {hasObs ? '✓' : '—'}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <EmptyState message="No completed meetings yet." />
                )}
              </div>
            </>
          )}

        </>
      )}
    </div>
  );
}
